import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Paper,
  Checkbox,
  Tooltip,
  Chip,
  Divider,
  ListItemText,
  Snackbar,
  Alert,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import SendIcon from "@mui/icons-material/Send";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AppsIcon from "@mui/icons-material/Apps";
import PersonIcon from "@mui/icons-material/Person";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import { useMsal } from "@azure/msal-react";
import {
  fetchRegionsAndPlants,
  fetchAllAdminApprovals,
  deleteAdminApprovalRecord,
  insertAdminApprovalRecord,
  fetchAdminAccessRecords,
  fetchAdminList,
} from "../../services/OutboundOrders/UserRolesServices";
import { getRolesForApplication } from "../../constants/roleConfig";
import { APPLICATION_LABEL_MAP } from "../../constants/userAccess";

const DEFAULT_APPLICATION = "OM - Load Optimization";

const BOTTLER_OPTIONS = [
  { value: "CCEP", label: "CCEP" },
  { value: "CCH", label: "CCH" },
  { value: "CCI", label: "CCI" },
];

const ROLE_TO_AD_GROUP_MAP = {
  Planner: "SEC-AZ-AL-TRUCKOPTIMIZATION-PLANNER",
  Bottler: "SEC-AZ-AL-TRUCKOPTIMIZATION-BOTTLER",
  Dispatcher: "SEC-AZ-AL-TRUCKOPTIMIZATION-DISPATCHER",
  "IT Support": "SEC-AZ-AL-TRUCKOPTIMIZATION-ITSUPPORT",
};

const INITIAL_FORM_STATE = {
  application: "",
  adGroup: "",
  selectedAdmins: [],
  levels: [{ levelNumber: 1, levelType: "Region", levelValues: [] }],
};

const isLoadOptimizationApp = (app = "") => {
  if (!app) return false;
  const normalized = String(app).toUpperCase().replace(/[^A-Z0-9]/g, "");
  return normalized.includes("LOAD") || normalized.includes("TRUCK");
};

const mapAdGroupToRole = (adGroup = "") => {
  if (!adGroup || adGroup === "null" || adGroup === "undefined") return "—";
  const upper = String(adGroup).toUpperCase().trim();

  if (upper.includes("PLANNER")) return "Planner";
  if (upper.includes("BOTTLER")) return "Bottler";
  if (
    upper.includes("ITSUPPORT") ||
    upper.includes("IT_SUPPORT") ||
    upper.includes("IT SUPPORT") ||
    upper.includes("SUPPORT") ||
    upper.includes("IT")
  ) {
    return "IT Support";
  }
  if (upper.includes("DISPATCHER") || upper.includes("DISPATCH"))
    return "Dispatcher";

  return adGroup.replace(/^SEC-AZ-AL-TRUCKOPTIMIZATION-/i, "").replace(/_/g, " ");
};

const formatDate = (dateString) => {
  if (!dateString) return "—";
  try {
    const [datePart] = String(dateString).split("T");
    if (!datePart) return String(dateString);
    const [year, month, day] = datePart.split("-");
    return `${day}/${month}/${year}`;
  } catch (e) {
    return String(dateString);
  }
};

const MultiSelectCheckbox = ({
  label,
  value = [],
  options = [],
  onChange,
  disabled = false,
  placeholder = "Select one or more",
}) => {
  const safeValue = Array.isArray(value) ? value : [];
  const allSelected = safeValue.length === options.length && options.length > 0;
  const selectAllValue = "SELECT_ALL_OPTION";

  const handleSelectAll = (event) => {
    event.stopPropagation();
    event.preventDefault();
    const optionValues = options.filter((o) => !o.disabled).map((o) => o.value);
    const newSelection = allSelected ? [] : optionValues;
    onChange({ target: { value: newSelection } });
  };

  const handleItemChange = (event) => {
    const selectedValues = event.target.value;
    const isSelectAllToggled = selectedValues.includes(selectAllValue);
    if (isSelectAllToggled) {
      if (allSelected) onChange({ target: { value: [] } });
      else {
        onChange({
          target: {
            value: options.filter((o) => !o.disabled).map((o) => o.value),
          },
        });
      }
    } else onChange(event);
  };

  return (
    <FormControl size="small" fullWidth disabled={disabled}>
      <Select
        id={label}
        multiple
        displayEmpty
        value={safeValue}
        onChange={handleItemChange}
        sx={{
          height: "34px",
          backgroundColor: "#ffffff",
          borderRadius: "8px",
          fontSize: "12px",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#d1d5db",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#9ca3af",
          },
        }}
        renderValue={(selected) => {
          if (selected.length === 0) {
            return (
              <Typography
                sx={{
                  color: "#9ca3af",
                  fontSize: "12px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {placeholder}
              </Typography>
            );
          }

          const selectedLabels = options
            .filter((opt) => selected.includes(opt.value))
            .map((opt) => opt.label);

          let displayText = "";
          if (selectedLabels.length === options.length) {
            displayText = "All Selected";
          } else if (selectedLabels.length > 1) {
            displayText = `${selectedLabels[0]}, +${selectedLabels.length - 1} more`;
          } else {
            displayText = selectedLabels.join(", ");
          }

          return (
            <Box
              component="span"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "block",
                fontSize: "12px",
              }}
            >
              {displayText}
            </Box>
          );
        }}
      >
        <MenuItem value={selectAllValue} onClick={handleSelectAll}>
          <Checkbox
            size="small"
            checked={allSelected}
            indeterminate={
              safeValue.length > 0 && safeValue.length < options.length
            }
          />
          <ListItemText
            primary="Select All"
            primaryTypographyProps={{ fontWeight: 600, fontSize: "12px" }}
          />
        </MenuItem>
        <Divider />
        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value} disabled={opt.disabled}>
            <Checkbox
              size="small"
              checked={safeValue.includes(opt.value)}
              disabled={opt.disabled}
            />
            <ListItemText primary={opt.label} primaryTypographyProps={{ fontSize: "12px" }} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const AdminPortal = () => {
  const { accounts } = useMsal();
  const currentUserEmail = useMemo(() => {
    return (accounts?.[0]?.username || accounts?.[0]?.name || "")
      .toLowerCase()
      .trim();
  }, [accounts]);

  const [userApplications, setUserApplications] = useState([]);
  const [selectedApplication, setSelectedApplication] = useState("");
  const [loadingApps, setLoadingApps] = useState(true);

  const [adminList, setAdminList] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(false);

  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);

  // Active filter criteria driven by Box 1 selects
  const [tableFilterApp, setTableFilterApp] = useState("");
  const [tableFilterRole, setTableFilterRole] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [regionOptions, setRegionOptions] = useState([]);
  const [plantsByRegion, setPlantsByRegion] = useState({});

  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  const handleCloseToast = () => setToast((prev) => ({ ...prev, open: false }));

  const isLoadOptSelected = isLoadOptimizationApp(selectedApplication);

  const availableRoles = useMemo(() => {
    if (!selectedApplication) return [];
    return getRolesForApplication(selectedApplication);
  }, [selectedApplication]);

  useEffect(() => {
    const loadAdminList = async () => {
      setLoadingAdmins(true);
      try {
        const response = await fetchAdminList();
        let rawAdmins = [];
        if (Array.isArray(response)) {
          rawAdmins = response;
        } else if (Array.isArray(response?.data)) {
          rawAdmins = response.data;
        } else if (Array.isArray(response?.data?.data)) {
          rawAdmins = response.data.data;
        } else if (response && typeof response === "object") {
          rawAdmins = [response];
        }

        const formattedAdmins = rawAdmins
          .map((item) => {
            const email = (
              item?.adGroupMemberEmail ||
              item?.adminEmail ||
              item?.email ||
              item?.ownerEmail ||
              item?.value ||
              ""
            ).trim();

            const name = (
              item?.adGroupMemberName ||
              item?.adminName ||
              item?.name ||
              item?.ownerName ||
              email
            ).trim();

            if (!email && !name) return null;

            const finalEmail = email || name;
            const finalName = name || email;

            return {
              value: finalEmail,
              name: finalName,
              email: finalEmail,
              label: `${finalName} (${finalEmail})`,
            };
          })
          .filter(Boolean);

        const uniqueAdmins = Array.from(
          new Map(formattedAdmins.map((a) => [a.value.toLowerCase(), a])).values()
        );

        setAdminList(uniqueAdmins);
      } catch (err) {
        console.error("Failed to load approver list:", err);
        setAdminList([]);
      } finally {
        setLoadingAdmins(false);
      }
    };

    loadAdminList();
  }, []);

  useEffect(() => {
    const loadUserApplications = async () => {
      setLoadingApps(true);
      try {
        const records = await fetchAdminAccessRecords();
        const allRecords = Array.isArray(records) ? records : [];

        const matchedApps = Array.from(
          new Set(
            allRecords
              .filter(
                (row) =>
                  row.adminEmail &&
                  row.adminEmail.toLowerCase().trim() === currentUserEmail,
              )
              .map((row) => row.application)
              .filter(Boolean),
          ),
        );

        if (matchedApps.length > 0) {
          setUserApplications(matchedApps);
          setSelectedApplication(matchedApps[0]);
          setTableFilterApp(matchedApps[0]);
        } else {
          setUserApplications([]);
          setSelectedApplication("");
          setTableFilterApp("");
        }
      } catch (err) {
        console.error("Failed to load user assigned applications:", err);
      } finally {
        setLoadingApps(false);
      }
    };

    if (currentUserEmail) {
      loadUserApplications();
    }
  }, [currentUserEmail]);

  useEffect(() => {
    if (selectedApplication) {
      const isLoadOpt = isLoadOptimizationApp(selectedApplication);

      setFormData((prev) => ({
        ...prev,
        application: selectedApplication,
        adGroup: "",
        selectedAdmins: [],
        levels: [
          {
            levelNumber: 1,
            levelType: isLoadOpt ? "Region" : "",
            levelValues: [],
          },
        ],
      }));
      setTableFilterApp(selectedApplication);
      setTableFilterRole("");
    }
  }, [selectedApplication]);

  useEffect(() => {
    const loadRegionsAndPlants = async () => {
      try {
        const regionsAndPlantsMapping = await fetchRegionsAndPlants();
        if (
          Array.isArray(regionsAndPlantsMapping) &&
          regionsAndPlantsMapping.length > 0
        ) {
          const regionSet = new Set();
          const plantDict = {};

          regionsAndPlantsMapping.forEach((item) => {
            const region = item.region;
            const plantName = item.plantName;

            if (!region) return;

            regionSet.add(region);
            if (!plantDict[region]) plantDict[region] = [];

            plantDict[region].push({
              value: plantName,
              label: plantName,
            });
          });

          setRegionOptions(
            Array.from(regionSet).map((r) => ({
              value: r,
              label: r,
            })),
          );
          setPlantsByRegion(plantDict);
        }
      } catch (err) {
        console.error("Failed to fetch regions and plants:", err);
      }
    };

    loadRegionsAndPlants();
  }, []);

  const loadAdminRecords = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchAllAdminApprovals("");

      let rawList = [];
      if (Array.isArray(response)) {
        rawList = response;
      } else if (Array.isArray(response?.data)) {
        rawList = response.data;
      } else if (Array.isArray(response?.data?.data)) {
        rawList = response.data.data;
      }

      const formattedRecords = rawList.map((item, idx) => {
        let displayL1 = "—";
        let displayL2 = "—";
        let displayL3 = "—";

        if (item.level1) {
          displayL1 = `Region: ${item.level1}`;
          if (item.level2) displayL2 = `Plant: ${item.level2}`;
          if (item.level3) displayL3 = `Bottler Company: ${item.level3}`;
        } else if (item.level2) {
          displayL1 = `Plant: ${item.level2}`;
          if (item.level3) displayL2 = `Bottler Company: ${item.level3}`;
        } else if (item.level3) {
          displayL1 = `Bottler Company: ${item.level3}`;
        }

        const rawApp = String(item?.application || selectedApplication || DEFAULT_APPLICATION);
        const mappedApp = APPLICATION_LABEL_MAP[rawApp] || rawApp;

        return {
          id: item?.id ? String(item.id) : `row-${idx}-${Date.now()}`,
          application: mappedApp,
          rawAdGroup: String(item?.adGroup || ""),
          role: mapAdGroupToRole(item?.adGroup),
          level1: displayL1,
          level2: displayL2,
          level3: displayL3,
          level4: "—",
          level5: "—",
          ownerName: String(item?.ownerName || "—"),
          ownerEmail: String(item?.ownerEmail || "—"),
          createdAt: item?.createdDate ? formatDate(item.createdDate) : "—",
        };
      });

      setTableData(formattedRecords);
    } catch (err) {
      console.error("Failed to load approver records:", err);
      setTableData([]);
    } finally {
      setLoading(false);
    }
  }, [selectedApplication]);

  useEffect(() => {
    loadAdminRecords();
  }, [loadAdminRecords]);

  // Box 1 selection handlers drive table filtering
  const handleBox1AppChange = (app) => {
    setSelectedApplication(app);
    setTableFilterApp(app);
    setTableFilterRole("");
  };

  const handleBox1RoleChange = (role) => {
    handleInputChange("adGroup", role);
    setTableFilterRole(role);
  };

  const filteredTableData = useMemo(() => {
    return tableData.filter((row) => {
      const matchApp = !tableFilterApp || row.application === tableFilterApp;
      const matchRole = !tableFilterRole || row.role === tableFilterRole;
      return matchApp && matchRole;
    });
  }, [tableData, tableFilterApp, tableFilterRole]);

  useEffect(() => {
    setPage(0);
  }, [tableFilterApp, tableFilterRole]);

  const paginatedTableData = useMemo(() => {
    return filteredTableData.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [filteredTableData, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isBottlerRole = formData.adGroup === "Bottler";

  const getAvailableLevelTypes = (levelIndex) => {
    if (!isLoadOptSelected) return [];

    const isLevel1 = levelIndex === 0;
    const isLevel2 = levelIndex === 1;
    const isLevel3 = levelIndex === 2;

    const level1Type = formData.levels[0]?.levelType;
    const level2Type = formData.levels[1]?.levelType;

    if (isLevel1) {
      return isBottlerRole
        ? ["Region", "Plant", "Bottler Company"]
        : ["Region", "Plant"];
    }

    if (isLevel2) {
      if (level1Type === "Region") {
        return isBottlerRole ? ["Plant", "Bottler Company"] : ["Plant"];
      }
      if (level1Type === "Plant") {
        return isBottlerRole ? ["Bottler Company"] : [];
      }
      if (level1Type === "Bottler Company") {
        return [];
      }
    }

    if (isLevel3) {
      if (level1Type === "Region" && level2Type === "Plant") {
        return isBottlerRole ? ["Bottler Company"] : [];
      }
      return [];
    }

    return [];
  };

  const maxAllowedLevels = useMemo(() => {
    if (!isLoadOptSelected) return 1;

    const level1Type = formData.levels[0]?.levelType;
    const level2Type = formData.levels[1]?.levelType;

    if (!level1Type) return 1;

    if (level1Type === "Region") {
      if (!isBottlerRole) return 2;
      if (level2Type === "Bottler Company") return 2;
      return 3;
    }

    if (level1Type === "Plant") {
      return isBottlerRole ? 2 : 1;
    }

    if (level1Type === "Bottler Company") {
      return 1;
    }

    return 1;
  }, [formData.levels, isBottlerRole, isLoadOptSelected]);

  const getValueOptionsForLevel = (
    levelIndex,
    currentLevels = formData.levels,
  ) => {
    if (!isLoadOptSelected) return [];

    const currentLevel = currentLevels[levelIndex];
    if (!currentLevel || !currentLevel.levelType) return [];

    if (currentLevel.levelType === "Region") {
      return regionOptions;
    }

    if (currentLevel.levelType === "Bottler Company") {
      return BOTTLER_OPTIONS;
    }

    if (currentLevel.levelType === "Plant") {
      const selectedRegions = currentLevels
        .filter((l) => l.levelType === "Region")
        .flatMap((l) => l.levelValues);

      if (selectedRegions.length > 0) {
        return selectedRegions.flatMap((r) => plantsByRegion[r] || []);
      }

      return Object.values(plantsByRegion).flat();
    }

    return [];
  };

  const handleRoleChange = (selectedRole) => {
    const defaultType = isLoadOptSelected ? "Region" : "";
    setFormData((prev) => ({
      ...prev,
      adGroup: selectedRole,
      selectedAdmins: [],
      levels: [{ levelNumber: 1, levelType: defaultType, levelValues: [] }],
    }));
  };

  const handleInputChange = (field, value) => {
    if (field === "adGroup") {
      handleRoleChange(value);
    } else {
      setFormData((prev) => ({ ...prev, [field]: value || "" }));
    }
  };

  const handleLevelTypeChange = (index, newLevelType) => {
    setFormData((prev) => {
      const updatedLevels = prev.levels.slice(0, index + 1);
      updatedLevels[index] = {
        ...updatedLevels[index],
        levelType: newLevelType,
        levelValues: [],
      };
      return { ...prev, levels: updatedLevels };
    });
  };

  const handleLevelValuesChange = (index, values) => {
    setFormData((prev) => {
      const updatedLevels = [...prev.levels];
      updatedLevels[index].levelValues = values || [];
      return { ...prev, levels: updatedLevels };
    });
  };

  const handleAddLevel = () => {
    if (!formData.adGroup || !isLoadOptSelected) return;

    if (formData.levels.length < maxAllowedLevels) {
      const nextLevelNumber = formData.levels.length + 1;
      const availableTypes = getAvailableLevelTypes(formData.levels.length);
      const defaultType = availableTypes[0] || "";

      setFormData((prev) => ({
        ...prev,
        levels: [
          ...prev.levels,
          {
            levelNumber: nextLevelNumber,
            levelType: defaultType,
            levelValues: [],
          },
        ],
      }));
    }
  };

  const handleRemoveLevel = (index) => {
    if (formData.levels.length > 1) {
      setFormData((prev) => ({
        ...prev,
        levels: prev.levels
          .filter((_, i) => i !== index)
          .map((lvl, idx) => ({
            ...lvl,
            levelNumber: idx + 1,
          })),
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedApplication) {
      setToast({
        open: true,
        message: "Please select an Application.",
        severity: "warning",
      });
      return;
    }

    if (!formData.adGroup) {
      setToast({
        open: true,
        message: "Please select a Role first.",
        severity: "warning",
      });
      return;
    }

    if (formData.selectedAdmins.length === 0) {
      setToast({
        open: true,
        message: "Please select at least one Approver.",
        severity: "warning",
      });
      return;
    }

    if (isLoadOptSelected) {
      const hasIncompleteLevel = formData.levels.some(
        (l) => l.levelValues.length === 0,
      );
      if (hasIncompleteLevel) {
        setToast({
          open: true,
          message: "Please select at least one Value for all configured levels.",
          severity: "warning",
        });
        return;
      }
    }

    const selectedAdminObjects = adminList.filter((admin) =>
      formData.selectedAdmins.includes(admin.value),
    );

    const regionObj = formData.levels.find((l) => l.levelType === "Region");
    const plantObj = formData.levels.find((l) => l.levelType === "Plant");
    const bottlerObj = formData.levels.find(
      (l) => l.levelType === "Bottler Company",
    );

    const targetAdGroup =
      ROLE_TO_AD_GROUP_MAP[formData.adGroup] || formData.adGroup;
    const currentRole = formData.adGroup;

    try {
      for (const admin of selectedAdminObjects) {
        const payload = {
          application: selectedApplication,
          adGroup: targetAdGroup,
          level1: isLoadOptSelected && regionObj?.levelValues?.length
            ? regionObj.levelValues.join(", ")
            : null,
          level2: isLoadOptSelected && plantObj?.levelValues?.length
            ? plantObj.levelValues.join(", ")
            : null,
          level3: isLoadOptSelected && bottlerObj?.levelValues?.length
            ? bottlerObj.levelValues.join(", ")
            : null,
          level4: null,
          ownerName: admin.name,
          ownerEmail: admin.email,
        };

        await insertAdminApprovalRecord(payload);
      }

      await loadAdminRecords();

      const defaultType = isLoadOptSelected ? "Region" : "";
      setFormData({
        application: selectedApplication,
        adGroup: currentRole,
        selectedAdmins: [],
        levels: [{ levelNumber: 1, levelType: defaultType, levelValues: [] }],
      });

      setToast({
        open: true,
        message: "Configuration added successfully!",
        severity: "success",
      });
    } catch (err) {
      console.error("Failed to insert approver record:", err);
      const failedEmails = selectedAdminObjects.map((a) => a.email).join(", ");
      const apiErrorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Conflict / Duplicate entry found.";

      setToast({
        open: true,
        message: `Failed for (${failedEmails}): ${apiErrorMessage}`,
        severity: "error",
      });

      const defaultType = isLoadOptSelected ? "Region" : "";
      setFormData({
        application: selectedApplication,
        adGroup: currentRole,
        selectedAdmins: [],
        levels: [{ levelNumber: 1, levelType: defaultType, levelValues: [] }],
      });
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedRows(filteredTableData.map((row) => row.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleSelectRow = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id],
    );
  };

  const handleSingleDelete = async (id) => {
    try {
      await deleteAdminApprovalRecord(id);
      setSelectedRows((prev) => prev.filter((rowId) => rowId !== id));
      await loadAdminRecords();
      setToast({
        open: true,
        message: "Record deleted successfully.",
        severity: "success",
      });
    } catch (err) {
      console.error(`Failed to delete record ${id}:`, err);
      setToast({
        open: true,
        message: "Failed to delete the record. Please try again.",
        severity: "error",
      });
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedRows.length === 0) return;

    try {
      const idsString = selectedRows.join(",");
      await deleteAdminApprovalRecord(idsString);
      setSelectedRows([]);
      await loadAdminRecords();
      setToast({
        open: true,
        message: "Selected records deleted successfully.",
        severity: "success",
      });
    } catch (err) {
      console.error("Failed to delete selected records:", err);
      setToast({
        open: true,
        message: "Failed to delete selected records.",
        severity: "error",
      });
    }
  };

  const isRoleSelected = Boolean(formData.adGroup);

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#ffffff" }}>
      {/* 1. Red Gradient Page Banner Header */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          background: "linear-gradient(90deg, #8E0E14 0%, #F40009 50%, #D40010 100%)",
          color: "#ffffff",
          px: { xs: 3, sm: 4 },
          pt: 3,
          pb: 4,
          mt: 0,
          boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "-96px",
            left: "33%",
            width: "60%",
            height: "224px",
            borderRadius: "50%",
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            filter: "blur(48px)",
            transform: "rotate(12deg)",
            pointerEvents: "none",
          }}
        />

        <Box sx={{ position: "relative", zIndex: 10 }}>
          <Typography
            variant="caption"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.8,
              fontSize: "0.68rem",
              fontWeight: 500,
              letterSpacing: "3px",
              textTransform: "uppercase",
              opacity: 0.9,
              mb: 0.2,
              mt: 2,
            }}
          >
            <Box
              component="span"
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor: "#ffffff",
              }}
            />
            USER ACCESS MANAGEMENT
          </Typography>

          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              fontSize: { xs: "1.5rem", sm: "1.9rem" },
              letterSpacing: "-0.025em",
              color: "#000000",
            }}
          >
            Approver Configuration
          </Typography>
        </Box>

        <Box
          component="svg"
          viewBox="0 0 1440 48"
          preserveAspectRatio="none"
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            height: "36px",
            color: "rgba(255, 255, 255, 0.10)",
            fill: "currentColor",
            pointerEvents: "none",
          }}
        >
          <path d="M0,10 C 260,42 480,0 760,14 C 1040,28 1240,2 1440,12 L1440,48 L0,48 Z" />
        </Box>

        <Box
          component="svg"
          viewBox="0 0 1440 48"
          preserveAspectRatio="none"
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            height: "28px",
            color: "rgba(255, 255, 255, 0.16)",
            fill: "currentColor",
            pointerEvents: "none",
          }}
        >
          <path d="M0,24 C 220,48 460,0 720,18 C 980,36 1220,4 1440,22 L1440,48 L0,48 Z" />
        </Box>
      </Box>

      {/* Main Page Content */}
      <Box
        sx={{
          padding: "24px 32px",
          fontFamily: "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif",
        }}
      >
        {/* 2. Configuration Card Section */}
        <Box
          sx={{
            backgroundColor: "#ffffff",
            border: "1px solid #e2e8f0",
            borderTop: "3px solid #dc2626",
            borderRadius: "16px",
            p: "20px 24px",
            mb: "24px",
            boxShadow: "0 10px 25px rgba(220, 38, 38, 0.12)",
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              fontSize: "13px",
              color: "#475569",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              mb: "20px",
              display: "flex",
              alignItems: "center",
              lineHeight: 1,
              "&::before": {
                content: '""',
                display: "inline-block",
                width: "4px",
                height: "16px",
                backgroundColor: "#dc2626",
                borderRadius: "2px",
                mr: "8px",
              },
            }}
          >
            Configure Approver & Access Levels
          </Typography>

          <form onSubmit={handleSubmit}>
            {/* Top Row: Application, Role, and Approvers Side-by-Side */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: "20px",
                mb: 2.5,
              }}
            >
              {/* Application Field (Top Header Filter) */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  width: "370px",
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 500,
                    color: "#334155",
                    whiteSpace: "nowrap",
                    mr: "8px",
                  }}
                >
                  Application:
                </Typography>
                <AppsIcon sx={{ fontSize: "16px", color: "#dc2626", mr: "8px" }} />
                <FormControl size="small" fullWidth disabled={loadingApps || userApplications.length === 0}>
                  <Select
                    value={selectedApplication}
                    displayEmpty
                    onChange={(e) => handleBox1AppChange(e.target.value)}
                    renderValue={(selected) => {
                      if (!selected) {
                        return (
                          <Typography sx={{ color: "#9ca3af", fontSize: "12px" }}>
                            Application *
                          </Typography>
                        );
                      }
                      return selected;
                    }}
                    sx={{
                      height: "34px",
                      backgroundColor: "#ffffff",
                      borderRadius: "8px",
                      fontSize: "12px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#d1d5db",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#9ca3af",
                      },
                    }}
                  >
                    {loadingApps ? (
                      <MenuItem disabled value="">
                        <em style={{ fontSize: "12px" }}>Loading Applications...</em>
                      </MenuItem>
                    ) : userApplications.length === 0 ? (
                      <MenuItem disabled value="">
                        <em style={{ fontSize: "12px" }}>No Assigned Applications Found</em>
                      </MenuItem>
                    ) : (
                      userApplications.map((app) => (
                        <MenuItem key={app} value={app} sx={{ fontSize: "12px" }}>
                          {app}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Box>

              {/* Role Field (Top Header Filter) */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  width: "350px",
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 500,
                    color: "#334155",
                    whiteSpace: "nowrap",
                    mr: "8px",
                  }}
                >
                  Role:
                </Typography>
                <PersonIcon sx={{ fontSize: "16px", color: "#dc2626", mr: "8px" }} />
                <FormControl size="small" fullWidth disabled={!selectedApplication}>
                  <Select
                    value={formData.adGroup || ""}
                    displayEmpty
                    onChange={(e) => handleBox1RoleChange(e.target.value)}
                    renderValue={(selected) => {
                      if (!selected) {
                        return (
                          <Typography sx={{ color: "#9ca3af", fontSize: "12px" }}>
                            Role *
                          </Typography>
                        );
                      }
                      return selected;
                    }}
                    sx={{
                      height: "34px",
                      backgroundColor: "#ffffff",
                      borderRadius: "8px",
                      fontSize: "12px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#d1d5db",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#9ca3af",
                      },
                    }}
                  >
                    {availableRoles.length === 0 ? (
                      <MenuItem disabled value="">
                        <em style={{ fontSize: "12px" }}>No Roles Configured</em>
                      </MenuItem>
                    ) : (
                      availableRoles.map((roleObj) => (
                        <MenuItem
                          key={roleOptValue(roleObj)}
                          value={getRoleValue(roleObj)}
                          sx={{ fontSize: "12px" }}
                        >
                          {getRoleLabel(roleObj)}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Box>

              {/* Approvers Field */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  width: "350px",
                  flexShrink: 0,
                }}
              >
                <Box sx={{ display: "inline-flex", alignItems: "center" }}>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: 500,
                      color: "#334155",
                      whiteSpace: "nowrap",
                      mr: "4px",
                    }}
                  >
                    Approvers:
                  </Typography>
                  <Tooltip
                    title="Please make sure the approver you wish to configure is added to the AD group as owner. The list auto updates at 9 AM UTC every day"
                    placement="top-start"
                    slotProps={{
                      popper: {
                        modifiers: [
                          {
                            name: "offset",
                            options: {
                              offset: [400, -1],
                            },
                          },
                        ],
                      },
                      tooltip: {
                        sx: {
                          fontSize: "12px",
                          whiteSpace: "nowrap",
                          maxWidth: "none",
                        },
                      },
                    }}
                  >
                    <InfoOutlinedIcon
                      fontSize="small"
                      sx={{ color: "#0288d1", cursor: "pointer", fontSize: "15px", mr: "8px" }}
                    />
                  </Tooltip>
                </Box>

                <VerifiedUserIcon sx={{ fontSize: "16px", color: "#dc2626", mr: "8px" }} />

                <Box sx={{ width: "300px", flexShrink: 0, mr: "16px" }}>
                  <MultiSelectCheckbox
                    label="Select Approvers"
                    value={formData.selectedAdmins || []}
                    options={adminList}
                    onChange={(e) =>
                      handleInputChange("selectedAdmins", e.target.value)
                    }
                    placeholder={
                      loadingAdmins
                        ? "Loading Approvers..."
                        : isRoleSelected
                        ? "Select Approvers..."
                        : "Select Role First"
                    }
                    disabled={!isRoleSelected || loadingAdmins}
                  />
                </Box>
              </Box>
            </Box>

            {/* Access Level Hierarchy Section */}
            <Box sx={{ width: "100%", textAlign: "left", mt: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 1.5,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#334155", fontSize: "12px", mr: "20px", minWidth: "140px" }}
                >
                  Access Level Hierarchy
                </Typography>

                {isLoadOptSelected && formData.levels.length < maxAllowedLevels && (
                  <Button
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={handleAddLevel}
                    size="small"
                    variant="text"
                    disabled={!isRoleSelected}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "12px",
                      color: "#dc2626",
                      p: 0,
                      minWidth: "auto",
                      "&:hover": {
                        backgroundColor: "transparent",
                        color: "#b91c1c",
                      },
                    }}
                  >
                    Add Level
                  </Button>
                )}
              </Box>

              {/* Levels Inputs */}
              {formData.levels.map((level, index) => {
                const availableLevelTypes = getAvailableLevelTypes(index);
                const valueOptions = getValueOptionsForLevel(index);
                const isLastLevel = index === formData.levels.length - 1;

                return (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 2,
                      width: "100%",
                      pl: 0,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        width: "300px",
                        mr: "20px",
                        flexShrink: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: 500,
                          color: "#334155",
                          whiteSpace: "nowrap",
                          mr: "8px",
                        }}
                      >
                        Level {level.levelNumber}:
                      </Typography>
                      <AccountTreeIcon sx={{ fontSize: "16px", color: "#dc2626", mr: "8px" }} />
                      <FormControl
                        size="small"
                        fullWidth
                        disabled={!isRoleSelected || !isLoadOptSelected}
                      >
                        <Select
                          value={level.levelType || ""}
                          displayEmpty
                          onChange={(e) =>
                            handleLevelTypeChange(index, e.target.value)
                          }
                          sx={{
                            height: "34px",
                            backgroundColor: "#ffffff",
                            borderRadius: "8px",
                            fontSize: "12px",
                          }}
                        >
                          {!isLoadOptSelected || availableLevelTypes.length === 0 ? (
                            <MenuItem value="">
                              <em style={{ fontSize: "12px" }}>—</em>
                            </MenuItem>
                          ) : (
                            availableLevelTypes.map((type) => (
                              <MenuItem key={type} value={type} sx={{ fontSize: "12px" }}>
                                {type}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                      </FormControl>
                    </Box>

                    <Box sx={{ width: "300px", flexShrink: 0, mr: "16px" }}>
                      <MultiSelectCheckbox
                        label={
                          level.levelType
                            ? `Select ${level.levelType}(s)`
                            : "Select Level Value"
                        }
                        value={level.levelValues || []}
                        options={valueOptions}
                        onChange={(e) =>
                          handleLevelValuesChange(index, e.target.value)
                        }
                        placeholder={
                          !isRoleSelected
                            ? "Select Role First"
                            : !isLoadOptSelected
                            ? "N/A for selected application"
                            : `Select ${level.levelType || "Value"}`
                        }
                        disabled={!isRoleSelected || !isLoadOptSelected}
                      />
                    </Box>

                    {/* Remove Level (-) Icon directly after the level value - ONLY ON LAST NODE */}
                    <Box
                      sx={{
                        width: "32px",
                        height: "32px",
                        display: "flex",
                        alignItems: "center",
                        justify: "center",
                      }}
                    >
                      {level.levelNumber > 1 && isLastLevel && (
                        <IconButton
                          color="error"
                          onClick={() => handleRemoveLevel(index)}
                          size="small"
                          disabled={!isRoleSelected}
                        >
                          <RemoveCircleOutlineIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>

                    {isLastLevel && (
                      <Button
                        type="submit"
                        variant="contained"
                        endIcon={<SendIcon sx={{ fontSize: "15px" }} />}
                        disabled={!isRoleSelected}
                        sx={{
                          backgroundColor: "#dc2626",
                          color: "#ffffff",
                          height: "34px",
                          minWidth: "150px",
                          flexShrink: 0,
                          ml: "auto",
                          "&:hover": {
                            backgroundColor: "#b91c1c",
                          },
                          textTransform: "none",
                          fontWeight: 600,
                          fontSize: "12px",
                          borderRadius: "8px",
                          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                        }}
                      >
                        Add Configuration
                      </Button>
                    )}
                  </Box>
                );
              })}
            </Box>
          </form>
        </Box>

        {/* 3. Data Table Section Box */}
        <Box
          sx={{
            backgroundColor: "#ffffff",
            border: "1px solid #e2e8f0",
            borderTop: "3px solid #dc2626",
            borderRadius: "16px",
            p: "20px 24px 0px 24px",
            boxShadow: "0 10px 25px rgba(220, 38, 38, 0.12)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justify: "space-between",
              alignItems: "center",
              width: "100%",
              mb: "18px",
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                fontSize: "13px",
                color: "#475569",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                display: "flex",
                alignItems: "center",
                lineHeight: 1,
                "&::before": {
                  content: '""',
                  display: "inline-block",
                  width: "4px",
                  height: "16px",
                  backgroundColor: "#dc2626",
                  borderRadius: "2px",
                  mr: "8px",
                },
              }}
            >
              Roles & Approvers Table
            </Typography>

            {selectedRows.length > 0 && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon sx={{ fontSize: "15px" }} />}
                onClick={handleDeleteSelected}
                size="small"
                sx={{
                  textTransform: "none",
                  fontSize: "11px",
                  height: "30px",
                  borderRadius: "6px",
                  ml: "auto",
                }}
              >
                Delete Selected ({selectedRows.length})
              </Button>
            )}
          </Box>

          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{
              borderRadius: "12px",
              borderColor: "#fecdd3",
              maxHeight: "calc(100vh - 280px)",
              overflow: "auto",
              boxShadow: "none",
            }}
          >
            <Table stickyHeader sx={{ minWidth: 1200, tableLayout: "fixed" }}>
              <TableHead sx={{ backgroundColor: "#fff0f2" }}>
                <TableRow sx={{ height: "30px" }}>
                  <TableCell padding="checkbox" sx={{ width: "3%", minWidth: "40px", py: "2px", backgroundColor: "#fff0f2" }}>
                    <Checkbox
                      size="small"
                      sx={{ color: "#881337", "&.Mui-checked": { color: "#881337" } }}
                      indeterminate={
                        selectedRows.length > 0 &&
                        selectedRows.length < paginatedTableData.length
                      }
                      checked={
                        paginatedTableData.length > 0 &&
                        selectedRows.length === paginatedTableData.length
                      }
                      onChange={handleSelectAll}
                    />
                  </TableCell>

                  {/* Static Header Label: APPLICATION */}
                  <TableCell sx={{ width: "15%", fontWeight: 600, color: "#881337", fontSize: "0.75rem", textTransform: "uppercase", px: 1, py: "2px", textAlign: "left", backgroundColor: "#fff0f2" }}>
                    APPLICATION
                  </TableCell>

                  {/* Static Header Label: ROLE */}
                  <TableCell sx={{ width: "9%", fontWeight: 600, color: "#881337", fontSize: "0.75rem", textTransform: "uppercase", px: 1, py: "2px", textAlign: "left", backgroundColor: "#fff0f2" }}>
                    ROLE
                  </TableCell>

                  <TableCell sx={{ width: "10%", fontWeight: 600, color: "#881337", fontSize: "0.75rem", textTransform: "uppercase", px: 1, py: "2px", textAlign: "left", backgroundColor: "#fff0f2" }}>
                    Level 1
                  </TableCell>
                  <TableCell sx={{ width: "10%", fontWeight: 600, color: "#881337", fontSize: "0.75rem", textTransform: "uppercase", px: 1, py: "2px", textAlign: "left", backgroundColor: "#fff0f2" }}>
                    Level 2
                  </TableCell>
                  <TableCell sx={{ width: "10%", fontWeight: 600, color: "#881337", fontSize: "0.75rem", textTransform: "uppercase", px: 1, py: "2px", textAlign: "left", backgroundColor: "#fff0f2" }}>
                    Level 3
                  </TableCell>
                  <TableCell sx={{ width: "8%", fontWeight: 600, color: "#881337", fontSize: "0.75rem", textTransform: "uppercase", px: 1, py: "2px", textAlign: "left", backgroundColor: "#fff0f2" }}>
                    Level 4
                  </TableCell>
                  <TableCell sx={{ width: "8%", fontWeight: 600, color: "#881337", fontSize: "0.75rem", textTransform: "uppercase", px: 1, py: "2px", textAlign: "left", backgroundColor: "#fff0f2" }}>
                    Level 5
                  </TableCell>
                  <TableCell sx={{ width: "10%", fontWeight: 600, color: "#881337", fontSize: "0.75rem", textTransform: "uppercase", px: 1, py: "2px", textAlign: "left", backgroundColor: "#fff0f2" }}>
                    Name
                  </TableCell>
                  <TableCell sx={{ width: "11%", fontWeight: 600, color: "#881337", fontSize: "0.75rem", textTransform: "uppercase", px: 1, py: "2px", textAlign: "left", backgroundColor: "#fff0f2" }}>
                    Email ID
                  </TableCell>
                  <TableCell sx={{ width: "8%", fontWeight: 600, color: "#881337", fontSize: "0.75rem", textTransform: "uppercase", px: 1, py: "2px", textAlign: "left", backgroundColor: "#fff0f2" }}>
                    Created Date
                  </TableCell>
                  <TableCell sx={{ width: "100px", minWidth: "100px", fontWeight: 600, color: "#881337", fontSize: "0.75rem", textTransform: "uppercase", px: 1, py: "2px", textAlign: "center", whiteSpace: "nowrap", backgroundColor: "#fff0f2" }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedTableData.length === 0 ? (
                  <TableRow style={{ height: 60 }}>
                    <TableCell colSpan={12} align="center" sx={{ py: 2, color: "text.secondary", fontSize: "12px" }}>
                      {loading
                        ? "Loading records..."
                        : "No configuration records found."}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedTableData.map((row) => {
                    const isItemSelected = selectedRows.includes(row.id);

                    return (
                      <TableRow key={row.id} hover selected={isItemSelected} sx={{ "& td": { py: "8px", px: 1, fontSize: "12px" } }}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={isItemSelected}
                            onChange={() => handleSelectRow(row.id)}
                          />
                        </TableCell>

                        <TableCell sx={{ px: 1, whiteSpace: "normal", wordBreak: "break-word", fontSize: "12px", textAlign: "left" }}>
                          <Tooltip title={row.application} placement="top-start" arrow>
                            <Box component="span" sx={{ cursor: "pointer" }}>
                              {row.application}
                            </Box>
                          </Tooltip>
                        </TableCell>

                        <TableCell sx={{ px: 1, textAlign: "left" }}>
                          <Chip
                            label={row.role || "—"}
                            size="small"
                            variant="outlined"
                            sx={{
                              color: "hsl(var(--foreground))",
                              borderColor: "hsl(var(--border))",
                              backgroundColor: "hsl(var(--secondary))",
                              fontWeight: 600,
                              fontSize: "11px",
                              height: "22px",
                            }}
                          />
                        </TableCell>

                        <TableCell sx={{ px: 1, whiteSpace: "normal", wordBreak: "break-word", fontSize: "12px", textAlign: "left" }}>
                          {row.level1}
                        </TableCell>
                        <TableCell sx={{ px: 1, whiteSpace: "normal", wordBreak: "break-word", fontSize: "12px", textAlign: "left" }}>
                          {row.level2}
                        </TableCell>
                        <TableCell sx={{ px: 1, whiteSpace: "normal", wordBreak: "break-word", fontSize: "12px", textAlign: "left" }}>
                          {row.level3}
                        </TableCell>
                        <TableCell sx={{ px: 1, whiteSpace: "normal", wordBreak: "break-word", fontSize: "12px", textAlign: "left" }}>
                          {row.level4}
                        </TableCell>
                        <TableCell sx={{ px: 1, whiteSpace: "normal", wordBreak: "break-word", fontSize: "12px", textAlign: "left" }}>
                          {row.level5}
                        </TableCell>

                        <TableCell sx={{ px: 1, whiteSpace: "normal", wordBreak: "break-word", fontSize: "12px", fontWeight: 500, textAlign: "left" }}>
                          {row.ownerName}
                        </TableCell>
                        <TableCell sx={{ px: 1, whiteSpace: "normal", wordBreak: "break-word", fontSize: "12px", textAlign: "left" }}>
                          {row.ownerEmail}
                        </TableCell>

                        <TableCell sx={{ px: 1, whiteSpace: "normal", wordBreak: "break-word", fontSize: "12px", textAlign: "left" }}>
                          {row.createdAt}
                        </TableCell>

                        <TableCell align="center" sx={{ px: 1 }}>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleSingleDelete(row.id)}
                              sx={{ p: "2px" }}
                            >
                              <DeleteIcon sx={{ fontSize: "16px" }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filteredTableData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              mt: 0.5,
              borderTop: "none",
              ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                fontSize: "12px",
              },
            }}
          />
        </Box>
      </Box>

      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseToast}
          severity={toast.severity}
          sx={{ width: "100%", fontSize: "12px" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const getRoleValue = (role) => (typeof role === "object" ? role.value : role);
const getRoleLabel = (role) => (typeof role === "object" ? role.label : role);
const roleOptValue = (role) => (typeof role === "object" ? role.value : role);

export default AdminPortal;